/**
 * TASALLOT-WAVE1-002 — Afghanistan Wave-1 Launch Contract (web client mirror).
 * Server ModuleAvailabilityService remains authoritative for API deny.
 * Env VITE_WAVE1_LAUNCH_ISOLATION=false disables client-side enforcement only.
 */

export type Wave1Classification =
  | "WAVE1_PUBLIC"
  | "WAVE1_INTERNAL"
  | "FUTURE_FROZEN"
  | "CTO_DECISION_REQUIRED";

export type CapabilityType =
  | "VERTICAL_MODULE"
  | "SHARED_PLATFORM_CAPABILITY"
  | "PROVIDER_SURFACE"
  | "ADMIN_OPERATIONS_SURFACE"
  | "FINANCIAL_INFRASTRUCTURE"
  | "FUTURE_MODULE";

export interface Wave1Capability {
  id: string;
  name: string;
  type: CapabilityType;
  classification: Wave1Classification;
  /** Customer home/nav/deep-link discovery */
  customerVisible: boolean;
  webCustomerEnabled: boolean;
  mobileCustomerEnabled: boolean;
  apiCustomerEnabled: boolean;
  internalEnabled: boolean;
  providerEnabled: boolean;
  financialExecutionEnabled: boolean;
  rationale: string;
}

export const WAVE1_COUNTRY_CODE = "AF";

const INTERNAL_ROLES = new Set([
  "admin",
  "super_admin",
  "country_admin",
  "province_admin",
  "department_admin",
  "support",
  "finance",
  "ops",
  "operations",
  "courier",
  "rider",
  "vendor",
  "seller",
  "merchant",
  "restaurant",
  "store",
  "warehouse_operator",
  "warehouse",
  "taxi_driver",
  "driver",
  "fleet_admin",
  "partner",
]);

/** Authoritative AF Wave-1 capability table */
export const WAVE1_CAPABILITIES: Record<string, Wave1Capability> = {
  marketplace: {
    id: "CAP-MARKETPLACE",
    name: "Marketplace",
    type: "VERTICAL_MODULE",
    classification: "WAVE1_PUBLIC",
    customerVisible: true,
    webCustomerEnabled: true,
    mobileCustomerEnabled: true,
    apiCustomerEnabled: true,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "Wave-1 candidate; ORDER031 Web purchase sealed (not full launch cert).",
  },
  food: {
    id: "CAP-FOOD",
    name: "Food",
    type: "VERTICAL_MODULE",
    classification: "WAVE1_PUBLIC",
    customerVisible: true,
    webCustomerEnabled: true,
    mobileCustomerEnabled: true,
    apiCustomerEnabled: true,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "Wave-1 candidate; NOT launch-certified.",
  },
  mart: {
    id: "CAP-MART",
    name: "Mart",
    type: "VERTICAL_MODULE",
    classification: "WAVE1_PUBLIC",
    customerVisible: true,
    webCustomerEnabled: true,
    mobileCustomerEnabled: true,
    apiCustomerEnabled: true,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "Wave-1 candidate; NOT launch-certified.",
  },
  grocery: {
    id: "CAP-GROCERY",
    name: "Grocery (Mart alias)",
    type: "VERTICAL_MODULE",
    classification: "WAVE1_PUBLIC",
    customerVisible: true,
    webCustomerEnabled: true,
    mobileCustomerEnabled: true,
    apiCustomerEnabled: true,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "Redirects to Mart; keep operable with Mart.",
  },
  pick: {
    id: "CAP-PICK",
    name: "Pick",
    type: "VERTICAL_MODULE",
    classification: "WAVE1_PUBLIC",
    customerVisible: true,
    webCustomerEnabled: true,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: true,
    internalEnabled: true,
    providerEnabled: false,
    financialExecutionEnabled: false,
    rationale:
      "WAVE1-004R9-RM1 customer Web thin projection over certified Pick APIs; local/cert only — not production tariff approval.",
  },
  courier: {
    id: "CAP-COURIER",
    name: "Courier / Rider",
    type: "PROVIDER_SURFACE",
    classification: "WAVE1_INTERNAL",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: true,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "Mandatory Wave-1 ops dependency; not public discovery; not launch-certified.",
  },
  seller_hub: {
    id: "CAP-SELLER_HUB",
    name: "Seller Hub",
    type: "PROVIDER_SURFACE",
    classification: "WAVE1_INTERNAL",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: true,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "Provider surface for Wave-1 verticals; not an independent customer vertical.",
  },
  taxi: {
    id: "CAP-TAXI",
    name: "Taxi",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "No CTO Wave-1 inclusion evidence; mock-heavy; freeze customer operation.",
  },
  hotels: {
    id: "CAP-HOTELS",
    name: "Hotels",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "Future train; not Wave-1 public.",
  },
  travel: {
    id: "CAP-TRAVEL",
    name: "Travel",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "Future train; demo-fare risk.",
  },
  real_estate: {
    id: "CAP-REAL_ESTATE",
    name: "Real Estate",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "Not in Wave-1 launch train.",
  },
  vehicles: {
    id: "CAP-VEHICLES",
    name: "Vehicles",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "Not in Wave-1 launch train.",
  },
  classifieds: {
    id: "CAP-CLASSIFIEDS",
    name: "Classifieds / Forsat",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "Not in Wave-1 launch train; mock-provider risk.",
  },
  export: {
    id: "CAP-EXPORT",
    name: "Export",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "Future train.",
  },
  kar: {
    id: "CAP-KAR",
    name: "Kar / Jobs",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "Future train; previously ungated API risk.",
  },
  jobs: {
    id: "CAP-JOBS",
    name: "Jobs (Kar alias)",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "Alias of Kar.",
  },
  ai: {
    id: "CAP-AI",
    name: "Tasallot AI",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: false,
    financialExecutionEnabled: false,
    rationale: "Future train.",
  },
  transport: {
    id: "CAP-TRANSPORT",
    name: "Transport Hub",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "Future train.",
  },
  pharmacy: {
    id: "CAP-PHARMACY",
    name: "Pharmacy",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "RESOLVED_BY_CTO (WAVE1-002A): FUTURE_FROZEN. Development may continue behind isolation.",
  },
  healthcare: {
    id: "CAP-HEALTHCARE",
    name: "Healthcare",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "RESOLVED_BY_CTO (WAVE1-002A): FUTURE_FROZEN.",
  },
  booking: {
    id: "CAP-BOOKING",
    name: "Booking",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "RESOLVED_BY_CTO (WAVE1-002A): FUTURE_FROZEN.",
  },
  wallet: {
    id: "CAP-WALLET",
    name: "Wallet",
    type: "FINANCIAL_INFRASTRUCTURE",
    classification: "WAVE1_INTERNAL",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: false,
    financialExecutionEnabled: false,
    rationale: "RESOLVED_BY_CTO (WAVE1-002A): WAVE1_INTERNAL. CUSTOMER_WALLET_PUBLIC_ENABLED=false; real money disabled.",
  },
  payments: {
    id: "CAP-PAYMENTS",
    name: "Online Payments Hub",
    type: "FINANCIAL_INFRASTRUCTURE",
    classification: "WAVE1_INTERNAL",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: false,
    financialExecutionEnabled: false,
    rationale: "RESOLVED_BY_CTO (WAVE1-002A): WAVE1_INTERNAL. Not public customer Wallet/Payments launch; commerce COD rails unchanged.",
  },
  recharge: {
    id: "CAP-RECHARGE",
    name: "Recharge",
    type: "FINANCIAL_INFRASTRUCTURE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: false,
    financialExecutionEnabled: false,
    rationale: "RESOLVED_BY_CTO (WAVE1-002A): FUTURE_FROZEN.",
  },
  gifts: {
    id: "CAP-GIFTS",
    name: "Gifts",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: false,
    financialExecutionEnabled: false,
    rationale: "Unguarded web surface; freeze customer access.",
  },
  rewards: {
    id: "CAP-REWARDS",
    name: "Rewards",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: false,
    financialExecutionEnabled: false,
    rationale: "Future train.",
  },
  stories: {
    id: "CAP-STORIES",
    name: "Stories",
    type: "SHARED_PLATFORM_CAPABILITY",
    classification: "WAVE1_INTERNAL",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: true,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "Platform rail; not independent customer vertical for Wave-1 discovery.",
  },
  utilities: {
    id: "CAP-UTILITIES",
    name: "Utilities",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: false,
    financialExecutionEnabled: false,
    rationale: "Future train.",
  },
  scratch_cards: {
    id: "CAP-SCRATCH",
    name: "Scratch Cards",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: false,
    financialExecutionEnabled: false,
    rationale: "Future / finance-adjacent; freeze public.",
  },
  learning: {
    id: "CAP-LEARNING",
    name: "Learning",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: false,
    financialExecutionEnabled: false,
    rationale: "Registry-only / not implemented.",
  },
  tourism: {
    id: "CAP-TOURISM",
    name: "Tourism",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: false,
    financialExecutionEnabled: false,
    rationale: "Not implemented.",
  },
  government: {
    id: "CAP-GOVERNMENT",
    name: "Government",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: false,
    financialExecutionEnabled: false,
    rationale: "Not implemented.",
  },
  gamified: {
    id: "CAP-GAMIFIED",
    name: "Gamified",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: false,
    financialExecutionEnabled: false,
    rationale: "Backend-only / not Wave-1 public.",
  },
  services: {
    id: "CAP-SERVICES",
    name: "Services",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: true,
    providerEnabled: true,
    financialExecutionEnabled: false,
    rationale: "Nested under classifieds; freeze with parent.",
  },
  pos: {
    id: "CAP-POS",
    name: "POS",
    type: "FUTURE_MODULE",
    classification: "FUTURE_FROZEN",
    customerVisible: false,
    webCustomerEnabled: false,
    mobileCustomerEnabled: false,
    apiCustomerEnabled: false,
    internalEnabled: false,
    providerEnabled: false,
    financialExecutionEnabled: false,
    rationale: "NOT_IMPLEMENTED; roadmap only.",
  },
};

export function isWave1IsolationEnabled(): boolean {
  const viteEnv =
    typeof import.meta !== "undefined"
      ? (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env?.VITE_WAVE1_LAUNCH_ISOLATION
      : undefined;
  const nodeEnv =
    typeof process !== "undefined" ? process.env?.WAVE1_LAUNCH_ISOLATION : undefined;
  const raw = viteEnv ?? nodeEnv;
  if (raw === undefined || raw === null || raw === "") return true;
  return raw !== "false" && raw !== "0" && raw !== "off";
}

export function normalizeModuleKey(key: string): string {
  return key.trim().toLowerCase().replace(/-/g, "_");
}

export function getWave1Capability(moduleKey: string): Wave1Capability | null {
  return WAVE1_CAPABILITIES[normalizeModuleKey(moduleKey)] ?? null;
}

export function getWave1Classification(moduleKey: string): Wave1Classification {
  const cap = getWave1Capability(moduleKey);
  return cap?.classification ?? "CTO_DECISION_REQUIRED";
}

export function isInternalOrProviderRole(role: string | undefined | null): boolean {
  if (!role) return false;
  return INTERNAL_ROLES.has(role.toLowerCase());
}

/** Country-scoped: AF Wave-1 customer deny for frozen / unresolved. */
export function isWave1CustomerDenied(moduleKey: string, countryCode: string): boolean {
  if (!isWave1IsolationEnabled()) return false;
  if (countryCode.toUpperCase() !== WAVE1_COUNTRY_CODE) return false;
  const cls = getWave1Classification(moduleKey);
  return cls === "FUTURE_FROZEN" || cls === "CTO_DECISION_REQUIRED";
}

/**
 * Customer nav / home visibility for AF.
 * WAVE1_PUBLIC only (INTERNAL hidden from customer discovery).
 */
export function isWave1CustomerNavVisible(moduleKey: string, countryCode: string): boolean {
  if (!isWave1IsolationEnabled()) return true;
  if (countryCode.toUpperCase() !== WAVE1_COUNTRY_CODE) return true;
  const cap = getWave1Capability(moduleKey);
  if (!cap) return false;
  return cap.classification === "WAVE1_PUBLIC" && cap.customerVisible;
}

/**
 * Surface access for web/mobile routes.
 * Customers: WAVE1_PUBLIC only (plus customerVisible INTERNAL if ever set).
 * Internal/provider: may access INTERNAL + FUTURE for development/ops.
 */
export function isWave1SurfaceAllowed(
  moduleKey: string,
  countryCode: string,
  role?: string | null,
): boolean {
  if (!isWave1IsolationEnabled()) return true;
  if (countryCode.toUpperCase() !== WAVE1_COUNTRY_CODE) return true;

  const cap = getWave1Capability(moduleKey);
  const cls = cap?.classification ?? "CTO_DECISION_REQUIRED";
  const internal = isInternalOrProviderRole(role);

  if (cls === "WAVE1_PUBLIC") return true;
  if (cls === "WAVE1_INTERNAL") {
    if (internal) return true;
    return !!cap?.customerVisible;
  }
  // FUTURE_FROZEN / CTO_DECISION_REQUIRED — fail closed for customers
  return internal;
}

/** API customer access (anonymous or customer role). */
export function isWave1ApiCustomerAllowed(moduleKey: string, countryCode: string): boolean {
  if (!isWave1IsolationEnabled()) return true;
  if (countryCode.toUpperCase() !== WAVE1_COUNTRY_CODE) return true;
  const cap = getWave1Capability(moduleKey);
  if (!cap) return false;
  if (cap.classification === "WAVE1_PUBLIC") return cap.apiCustomerEnabled;
  if (cap.classification === "WAVE1_INTERNAL") return cap.apiCustomerEnabled;
  return false;
}

export function wave1DenyReason(moduleKey: string): string {
  const cls = getWave1Classification(moduleKey);
  return cls === "CTO_DECISION_REQUIRED" ? "WAVE1_CTO_DECISION_REQUIRED" : "WAVE1_FUTURE_FROZEN";
}

/** WAVE1-002A CTO: wallet is INTERNAL but not a public customer launch */
export const CUSTOMER_WALLET_PUBLIC_ENABLED = false;
/** WAVE1-002A CTO: payments hub is INTERNAL but not a public customer module launch */
export const CUSTOMER_PAYMENTS_MODULE_PUBLIC_ENABLED = false;
export const REAL_MONEY_ENABLED = false;
export const BANK_PSP_ENABLED = false;

export const EFFECTIVE_MODULE_AVAILABILITY_CONTRACT =
  "available = country_active AND module_status_active AND role_authorized AND wave1_classification_allows AND environment_safety; client flags NEVER override server deny; FUTURE_FROZEN|CTO_DECISION_REQUIRED => deny customer (AF); WAVE1_INTERNAL customer API only if apiCustomerEnabled";
